// Asset-scoped grants API hooks (spec 2026-09-04-03).
//
// A grant is (user, role, anchor asset, validity). Typed through the generated
// client, like roles.ts.

import { useQueryClient } from '@tanstack/react-query'
import { $api } from './queryClient'
import type { components } from './schema'

export type AccessGrant = components['schemas']['AccessGrant']
export type CreateAccessGrantRequest = components['schemas']['CreateAccessGrantRequest']
export type RoleReach = components['schemas']['RoleReach']

/** The built-in roles that may only be granted on an asset (decision 10). */
export const SCOPED_ONLY_ROLE_CODES = ['resident', 'property_owner', 'provider'] as const

/** The membership role a scoped-only holder carries. */
export const GUEST_ROLE_CODE = 'guest'

/** Every grant of the organization, optionally filtered. */
export function useAccessGrants(
    org: string,
    filters?: { user_uid?: string; asset_uid?: string; include_expired?: boolean },
    enabled = true
) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/access-grants',
        {
            params: {
                path: { orgCode: org },
                query: filters as Record<string, never>,
            },
        },
        { enabled: enabled && Boolean(org) }
    )
}

/** Who has access to one asset, including the grants inherited from above. */
export function useAssetAccess(org: string, assetUid: string | undefined, enabled = true) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/access',
        { params: { path: { orgCode: org, assetUid: assetUid ?? '' } } },
        { enabled: enabled && Boolean(org) && Boolean(assetUid) }
    )
}

/**
 * invalidateAccess drops every access-grant query of the org. Grants are read
 * from two different endpoints (the org list and the per-asset one) and a
 * revocation has to move both, so this matches on the path prefix rather than
 * enumerating them. Granting a role to an unknown e-mail creates an invitation
 * instead, so the members page's pending invitations move too.
 */
function useInvalidateAccess(org: string) {
    const queryClient = useQueryClient()
    return () => {
        void queryClient.invalidateQueries({ queryKey: ['invitations', org] })
        void queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                if (key.length < 2 || key[0] !== 'get') return false
                const path = key[1]
                if (typeof path !== 'string') return false
                if (
                    path !== '/api/v1/organizations/{orgCode}/access-grants' &&
                    path !== '/api/v1/organizations/{orgCode}/assets/{assetUid}/access' &&
                    path !== '/api/v1/organizations/{orgCode}/members' &&
                    path !== '/api/v1/users/me/organizations'
                ) {
                    return false
                }
                const params = key.find(
                    (item) => item && typeof item === 'object' && 'params' in item
                ) as { params?: { path?: { orgCode?: string } } } | undefined
                return !params?.params?.path?.orgCode || params.params.path.orgCode === org
            },
        })
    }
}

/** Grant a role on one asset — or invite the e-mail carrying that grant. */
export function useCreateAccessGrant(org: string) {
    const invalidate = useInvalidateAccess(org)
    return $api.useMutation('post', '/api/v1/organizations/{orgCode}/access-grants', {
        onSuccess: invalidate,
    })
}

/** Revoke a grant. Soft-deletes it, so the audit trail keeps the history. */
export function useRevokeAccessGrant(org: string) {
    const invalidate = useInvalidateAccess(org)
    return $api.useMutation('delete', '/api/v1/organizations/{orgCode}/access-grants/{grantUid}', {
        onSuccess: invalidate,
    })
}

/** Change a grant's validity or note. */
export function useUpdateAccessGrant(org: string) {
    const invalidate = useInvalidateAccess(org)
    return $api.useMutation('patch', '/api/v1/organizations/{orgCode}/access-grants/{grantUid}', {
        onSuccess: invalidate,
    })
}
